import { useEffect, useState } from "react";
import { FileSignature, Loader2, Send, CheckCircle2, Clock, FileText, Mail, RefreshCw, Info, RotateCcw, Bell, Printer, PenLine, Layers, ArrowRight, Sparkles, Settings, AlertTriangle, History } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { previewPdfResponse, customerRecipient } from "../../../lib/pdfPreview";
import { isoWeekMonday } from "../../../lib/deliveryWeek";
import { useAuth } from "../../../lib/auth";
import { flashMissingByTestId } from "../../../lib/requiredField";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { AutosaveBadge } from "./useAutosaveOnBlur";
import { DispoBackButton } from "./DispoBackButton";
import { FeatureGuideTriangle } from "./FeatureGuideTriangle";
import { OnSiteSignatureModal } from "./OnSiteSignatureModal";
import { useGearsVisible } from "../../../lib/gearVisibility";

// ---------- Phase 6 — Kaufvertrag, Anhänge & digitale Signatur ----------------
export function ContractSignModal({ listingId, hasCustomer, hasTradein = false, listingData = null, onBack, onClose, onSaved, onSent, onContinue }) {
    const gearsOk = useGearsVisible();

    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [localCust, setLocalCust] = useState(false);
    const [sending, setSending] = useState(false);
    const [signing, setSigning] = useState(false);
    const [signPortal, setSignPortal] = useState(null);
    const [portalBusy, setPortalBusy] = useState(false);
    // Jul-2026 (User-Wunsch) — Digitale Signatur nur, wenn in den Händler-
    // Einstellungen aktiviert; sonst Buttons ausgegraut.
    const [sigEnabled, setSigEnabled] = useState(null);
    useEffect(() => {
        api.get(`/users/me/dealer-flags?listing_id=${listingId}`)
            .then((r) => setSigEnabled(Boolean(r.data?.digital_signature_enabled)))
            .catch(() => setSigEnabled(false));
    }, [listingId]);
    const [withSA, setWithSA] = useState(true);
    // Jun-2026 (User-Wunsch c) — Pro-Vorgang-Override: Montageauftrag jetzt erzeugen.
    // Vorbelegt mit der globalen Händler-Einstellung „Montageauftrag früh erzeugen".
    const { user } = useAuth();
    const [createWoNow, setCreateWoNow] = useState(false);
    useEffect(() => { setCreateWoNow(!!user?.dispo_auto_workshop_order_early); }, [user?.dispo_auto_workshop_order_early]);
    // Feb-2026 (User-Wunsch) — Auslieferungstermin: „Unverbindlich (Bestellfahrzeug)"
    // ist standardmäßig angehakt und vorerst NICHT veränderbar.
    const [deliveryDate, setDeliveryDate] = useState("");
    const [deliveryBinding, setDeliveryBinding] = useState(false);
    const [deliverySaving, setDeliverySaving] = useState(false);
    const [draftStatus, setDraftStatus] = useState("idle"); // Auto-Speichern-Status (Auslieferungstermin)
    const [draftSavedAt, setDraftSavedAt] = useState(null);
    // Jun-2026 (User-Wunsch · Msg 509) — „Unverbindlich reservieren" aus dem
    // Zahlungs-Modal hierher verschoben (Kaufvertrag-Schritt).
    const [hasFinancing, setHasFinancing] = useState(false);
    // Jul-2026 (User-Wunsch) — EU-steuerfreier Verkauf: zusätzliche Pflicht-Dokumente.
    const [euTaxFree, setEuTaxFree] = useState(false);
    const [reserving, setReserving] = useState(false);
    const [resHours, setResHours] = useState(48);
    // Jul-2026 (User-Wunsch) — Zahnrad: org-weite Reservierungs-Regeln.
    const [resSettingsOpen, setResSettingsOpen] = useState(false);
    const [resSettings, setResSettings] = useState(null);
    const [extending, setExtending] = useState(false);
    const [resExtended, setResExtended] = useState(false);
    const [resMsg, setResMsg] = useState("");
    const [showResMsg, setShowResMsg] = useState(false);
    const [resAiBusy, setResAiBusy] = useState(false);
    // Jul-2026 (User-Wunsch) — Kundendaten für die persönliche Anrede im E-Mail-Text.
    const [custData, setCustData] = useState(null);
    const [resInfo, setResInfo] = useState(false);
    // Jun-2026 (User-Wunsch) — „Reservieren & E-Mail" als Haken: ist er gesetzt,
    // löst der Footer-Button „An Käufer senden" zusätzlich die Reservierung +
    // Reservierungs-E-Mail aus (ein Klick = reservieren + Vertrag senden).
    const [reserveOnSend, setReserveOnSend] = useState(false);
    // Aug-2026 (User-Wunsch): Haken „Auf Genehmigung und Freigabe warten" ist
    // frei wählbar (Standard: angehakt). Angehakt → Reservierungs-Block wird
    // ausgeblendet; abgewählt → Reservierungs-Block erscheint (und umgekehrt
    // blendet „Unverbindlich reservieren" den Freigabe-Block aus).
    const [approvalChecked, setApprovalChecked] = useState(true);
    // Jun-2026 (User-Wunsch) — Live-Countdown der aktiven Reservierung.
    const [reservedUntil, setReservedUntil] = useState(null);
    const [reservationStatus, setReservationStatus] = useState(null);
    const [nowTs, setNowTs] = useState(Date.now());
    const [resettingSig, setResettingSig] = useState(false);
    // Jun-2026 (User-Wunsch) — Vor-Ort-Unterschrift (Bildschirm/Tablet) + „Alles drucken".
    const [sigModal, setSigModal] = useState(null); // "contract" | "tradein" | null
    const [signingOnsite, setSigningOnsite] = useState(false);
    // Jun-2026 (User-Wunsch) — Erneute Freigabe für das Gesamtgeschäft (unter
    // Auslieferungstermin). Haken „Auf Genehmigung und Freigabe warten" ist fix.
    const [dealApproval, setDealApproval] = useState(null);
    const [dealApprovalBusy, setDealApprovalBusy] = useState(false);
    // Jun-2026 (User-Wunsch) — Auftragsbestätigung (nach Freigabe).
    const [ocMsg, setOcMsg] = useState("");
    const [showOcMsg, setShowOcMsg] = useState(false);
    const [ocBusy, setOcBusy] = useState(false);
    const base = `${process.env.REACT_APP_BACKEND_URL}/api/listings/${listingId}/dispo`;

    const load = async () => {
        // Jun-2026 — entkoppelt: contract-status & payment laufen unabhängig; das
        // `loading`-Flag (gated die Freigabe/Auftragsbestätigung-Sektion) hängt NUR
        // am Listing-Fetch, der den Freigabe-Status trägt. Verhindert, dass eine
        // langsame Neben-Anfrage die Freigabe-Anzeige dauerhaft blockiert.
        api.get(`/listings/${listingId}/dispo/contract/status`)
            .then((r) => setState(r.data && r.data.contract_number ? r.data : null))
            .catch(() => {});
        api.get(`/listings/${listingId}/dispo/payment`)
            .then((r) => {
                setHasFinancing((r.data?.payments || []).some((p) => p.method === "finanzierung" || p.method === "leasing"));
                setEuTaxFree(Boolean(r.data?.eu_tax_free));
            })
            .catch(() => {});
        try {
            // Jul-2026 (Iter-592-Fix): listingData kommt bereits vom Parent —
            // eigener Fetch nur als Fallback (vermeidet Mount-Race unter StrictMode).
            const lst = listingData || await api.get(`/listings/${listingId}`).then((r) => r.data).catch(() => null);
            setLocalCust(Boolean(lst?.dispo_customer?.email));
            setCustData(lst?.dispo_customer || null);
            const dv = lst?.dispo_delivery || {};
            // Jul-2026 (User-Wunsch) — Vorbefüllung: ohne gespeicherten Termin wird
            // das „Frühestens"-Lieferdatum des Fahrzeugs (KW.Jahr) übernommen.
            if (dv.delivery_date) {
                setDeliveryDate(dv.delivery_date);
            } else {
                const m = /^(\d{1,2})\.(\d{4})$/.exec(String(lst?.earliest_delivery || "").trim());
                const monday = m ? isoWeekMonday(Number(m[1]), Number(m[2])) : null;
                if (monday) {
                    // Nur anzeigen — gespeichert wird erst bei bewusster Aktion des Nutzers.
                    setDeliveryDate(monday.toISOString().slice(0, 10));
                }
            }
            // Vorerst immer unverbindlich (User-Wunsch) — nicht veränderbar.
            setDeliveryBinding(false);
            // Aktive Reservierung für den Live-Countdown.
            setReservedUntil(lst?.reserved_until || null);
            setResExtended(Boolean(lst?.dispo_reserve_extended));
            setReservationStatus(lst?.reservation_status || null);
            setDealApproval(lst?.dispo_deal_approval || null);
            // Aug-2026: läuft bereits eine Reservierung ohne Freigabe-Vorgang,
            // startet der Haken abgewählt (Countdown bleibt sichtbar).
            const resActiveNow = Boolean(lst?.reserved_until) && new Date(lst.reserved_until) > new Date();
            setApprovalChecked(Boolean(lst?.dispo_deal_approval) || !resActiveNow);
        } finally { setLoading(false); }
        loadApprovalHistory();
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [listingId]);

    // Jun-2026 (User-Wunsch) — Sekundentakt für den Reservierungs-Countdown.
    useEffect(() => {
        if (!reservedUntil) return undefined;
        const t = setInterval(() => setNowTs(Date.now()), 1000);
        return () => clearInterval(t);
    }, [reservedUntil]);

    const resRemainingMs = reservedUntil ? (new Date(reservedUntil).getTime() - nowTs) : 0;
    const resActive = Boolean(reservedUntil) && resRemainingMs > 0;
    const fmtRemaining = (ms) => {
        const tot = Math.max(0, Math.floor(ms / 1000));
        const d = Math.floor(tot / 86400);
        const h = Math.floor((tot % 86400) / 3600);
        const m = Math.floor((tot % 3600) / 60);
        const s = tot % 60;
        return [d ? `${d} T` : null, `${h} Std.`, `${m} Min.`, `${s} Sek.`].filter(Boolean).join(" ");
    };
    const fmtUntil = (iso) => { try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

    // Mit der Reservierungs-E-Mail mitgesendete Unterlagen (Finanzierung → Selbstauskunft).
    const enclosedDocs = ["Kaufvertrag", "Datenschutzhinweise", "AGB", ...(hasFinancing ? ["Selbstauskunft (Finanzierung)"] : [])];
    // Jul-2026 (User-Wunsch) — neuer Standardtext für die Reservierungs-E-Mail
    // (persönliche Anrede, Firma, Reservierungsdauer; Finanzierungs-Absatz nur bei
    // Zahlungsart Finanzierung; Upload-Portal-Link wird automatisch angehängt).
    const defaultResMsg = () => {
        const anrede = custData?.salutation === "Frau" ? "Sehr geehrte Frau" : (custData?.salutation === "Herr" ? "Sehr geehrter Herr" : "Guten Tag");
        const name = (custData?.last_name || "").trim();
        const firma = user?.company_name || "unserem Haus";
        const finBlock = hasFinancing
            ? "\n\nZur Erfassung des Finanzierungs-/Leasingantrages fügen wir im Anhang die benötigte Selbstauskunft bei."
            : "";
        return `${anrede}${name ? ` ${name}` : ""},

vielen Dank für das angenehme Telefonat.

Wie besprochen sende ich Ihnen die verbindliche Bestellung (bis zur Unterschrift bleibt es eine Reservierung für ${Number(resHours) || 48} Stunden) mit der ${firma} sowie die Datenschutzerklärung zur Unterschrift — mit der Bitte um zeitnahe Rückgabe über unser Upload-Portal (Link am Ende dieser E-Mail).

Alle unsere Absprachen finden Sie im Vertrag unter „Zahlungshinweise und sonstige Vereinbarungen“.${finBlock}

Außerdem benötigen wir folgende Unterlagen über unser Upload-Portal:
• Ausweis oder Reisepass (mit Meldebescheinigung) in Kopie, beide Seiten mit separater originaler Unterschrift sowie Ort und Datum (bei Firmen vom Geschäftsführer) zur Identifikation; bei Selbstständigen die Gewerbeanmeldung / den Handelsregisterauszug in Kopie.
• Ihre IBAN von dem Konto, von dem Sie die Überweisung tätigen, damit wir die Zahlung zuordnen können. Um Schreibfehler zu vermeiden, am besten auch die EC-Karte beidseitig in Kopie.
• Überweisung an das im Kaufvertrag genannte Konto unter Angabe der Bestands- und/oder Fahrgestellnummer aus der verbindlichen Bestellung.
• Upload des Screenshots der getätigten Zahlung, damit die weitere Bearbeitung schnellstens erfolgen kann.

Wir empfehlen zur kompletten Sorglosigkeit: Kostensicherheit für Inspektionen und Reparaturen, Garantieverlängerung sowie Zubehör zum Komplettpreis. Alle Zusatzleistungen oder Wünsche können auch in die Finanzierungs- oder Leasingrate eingerechnet werden.

Für Rückfragen stehe ich Ihnen gerne jederzeit zur Verfügung.

Mit freundlichen Grüßen
${firma}`;
    };
    const openResMsg = () => { setResMsg((m) => m || defaultResMsg()); setShowResMsg((s) => !s); };

    // Jul-2026 (User-Wunsch) — KI überarbeitet den Reservierungs-E-Mail-Text.
    const resAiImprove = async () => {
        setResAiBusy(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/reserve/suggest-message`, { current_text: resMsg || defaultResMsg() });
            if (r.data?.text) { setResMsg(r.data.text); toast.success("KI-Text eingefügt — bitte prüfen"); }
        } catch (e) { toast.error(e.response?.data?.detail || "KI-Vorschlag fehlgeschlagen"); }
        finally { setResAiBusy(false); }
    };

    // Verlängerungs-Button braucht die Settings (Monatszahl) auch ohne Zahnrad-Klick.
    useEffect(() => {
        if (resActive && !resSettings) {
            api.get("/dealer/reserve-settings").then((r) => setResSettings(r.data)).catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resActive]);

    const openResSettings = async () => {
        setResSettingsOpen((s) => !s);
        if (!resSettings) {
            try { const r = await api.get("/dealer/reserve-settings"); setResSettings(r.data); } catch { setResSettings({ max_months: 3, extension_months: 1 }); }
        }
    };
    const saveResSettings = async () => {
        try {
            const r = await api.patch("/dealer/reserve-settings", resSettings);
            setResSettings({ max_months: r.data.max_months, extension_months: r.data.extension_months });
            toast.success("Reservierungs-Regeln gespeichert — gelten für alle Standorte");
        } catch { toast.error("Speichern fehlgeschlagen"); }
    };
    const extendReservation = async () => {
        setExtending(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/reserve/extend`);
            setReservedUntil(r.data.reserved_until);
            setResExtended(true);
            toast.success(`Reservierung einmalig um ${r.data.months} Monat(e) verlängert${r.data.email_sent ? " — Kunde informiert" : ""}`);
            await onSaved?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Verlängerung fehlgeschlagen"); }
        finally { setExtending(false); }
    };

    const reserve = async () => {
        setReserving(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/reserve`, {
                hours: Number(resHours) || 48,
                message: resMsg || undefined,
                financing: hasFinancing,
                documents: enclosedDocs,
            });
            toast.success(`Reserviert für ${r.data.hours}h · E-Mail ${r.data.email_mocked ? "(Demo/Mock)" : "versendet"}`);
            setReservedUntil(r.data.reserved_until || null);
            setReservationStatus("active");
            await onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Reservierung fehlgeschlagen");
        } finally { setReserving(false); }
    };

    // Jun-2026 (User-Wunsch) — Signatur zurücksetzen (Status → ausstehend).
    const resetSignature = async () => {
        setResettingSig(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/contract/reset-signature`);
            setState(r.data.dispo_contract);
            toast.message("Signatur zurückgesetzt — Vertrag kann erneut signiert werden");
            await onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zurücksetzen fehlgeschlagen");
        } finally { setResettingSig(false); }
    };

    // Jun-2026 (User-Wunsch) — Vor-Ort-Unterschrift speichern (Kaufvertrag ODER Ankaufvertrag).
    const submitOnsite = async (sigs, place) => {
        if (sigModal === "contract" && !requireDeliveryDate()) { setSigModal(null); return; }
        setSigningOnsite(true);
        try {
            if (sigModal === "tradein") {
                await api.post(`/listings/${listingId}/dispo/tradein/sign-onsite`, {
                    seller_signature: sigs.seller || "", buyer_signature: sigs.buyer || "", place,
                });
                toast.success("Ankaufvertrag vor Ort unterschrieben");
            } else if (sigModal === "handover") {
                await api.post(`/listings/${listingId}/dispo/handover/sign-onsite`, {
                    buyer_signature: sigs.buyer || "", seller_signature: sigs.seller || "", place,
                });
                toast.success("Übergabeprotokoll vor Ort unterschrieben");
            } else {
                await api.post(`/listings/${listingId}/dispo/contract/sign-onsite`, {
                    buyer_signature: sigs.buyer || "", seller_signature: sigs.seller || "", place,
                });
                toast.success("Kaufvertrag vor Ort unterschrieben");
            }
            setSigModal(null);
            await load();
            await onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Unterschrift konnte nicht gespeichert werden");
        } finally { setSigningOnsite(false); }
    };

    // Jul-2026 (User-Report „Vorschau funktioniert nicht") — PDFs werden jetzt
    // authentifiziert geladen und in der Vorschau angezeigt; Fehler (z. B.
    // fehlende Käuferdaten) erscheinen als verständliche Toast-Meldung.
    const openPdf = async (path, filename) => {
        try {
            const r = await api.get(path, { responseType: "blob" });
            previewPdfResponse(r, filename, { recipient: customerRecipient(custData) });
        } catch (e) {
            let msg = "Vorschau fehlgeschlagen";
            try { msg = JSON.parse(await e.response.data.text())?.detail || msg; } catch { /* noop */ }
            toast.error(msg);
        }
    };

    const saveDelivery = async (date, binding, { silent = true } = {}) => {
        setDeliverySaving(true);
        setDraftStatus("saving");
        try {
            await api.patch(`/listings/${listingId}/dispo/delivery`, {
                delivery_date: date || null,
                binding,
            });
            setDraftStatus("saved");
            setDraftSavedAt(new Date());
            if (!silent) toast.success("Auslieferungstermin aktualisiert");
            return true;
        } catch (e) {
            setDraftStatus("idle");
            toast.error(e.response?.data?.detail || "Auslieferungstermin konnte nicht gespeichert werden");
            return false;
        } finally { setDeliverySaving(false); }
    };

    // Aug-2026 (User-Wunsch) — Die Freigabe wird AUTOMATISCH angefordert, sobald
    // ALLE Pflicht-Unterlagen im Upload vorhanden sind (Backend-Trigger). Der
    // Button dient nur noch als manuelle Anforderung/Erinnerung.
    const requestDealApproval = async () => {
        setDealApprovalBusy(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/deal/request-approval`);
            setDealApproval(r.data?.approval || { status: "pending" });
            toast.success("Freigabe beim Filialleiter angefordert");
            loadApprovalHistory();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Freigabe konnte nicht angefordert werden");
        } finally { setDealApprovalBusy(false); }
    };

    // Aug-2026 (User-Wunsch) — Freigabe-Verlauf am Fahrzeug (Anforderungen,
    // Freigaben, Ablehnungen mit Grund, Datum und Prüfer).
    const [approvalHistory, setApprovalHistory] = useState([]);
    const [historyOpen, setHistoryOpen] = useState(false);
    const loadApprovalHistory = () => {
        api.get(`/listings/${listingId}/dispo/approval-history`)
            .then((r) => setApprovalHistory(r.data?.items || []))
            .catch(() => { /* optional */ });
    };

    // Jun-2026 (User-Wunsch) — Auftragsbestätigung (erneut/editiert) senden.
    const sendOrderConfirmation = async () => {
        setOcBusy(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/deal/order-confirmation`, { custom_message: ocMsg });
            setDealApproval((p) => ({ ...(p || {}), confirmation_sent_at: r.data?.confirmation_sent_at, confirmation_message: r.data?.confirmation_message }));
            setShowOcMsg(false);
            toast.success("Auftragsbestätigung versendet");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Auftragsbestätigung fehlgeschlagen");
        } finally { setOcBusy(false); }
    };

    // Aug-2026 (User-Wunsch): KEIN Auto-Anfordern mehr beim Öffnen — die Freigabe
    // wird vom Backend automatisch angestoßen, sobald alle Unterlagen im Upload
    // vollständig sind.


    // Jul-2026 (User-Wunsch) — Auslieferungstermin ist PFLICHTFELD: fehlt er,
    // wird direkt zum Feld gesprungen und dieses animiert.
    const requireDeliveryDate = () => {
        if ((deliveryDate || "").trim()) return true;
        toast.error("Bitte den Auslieferungstermin erfassen — Pflichtfeld.");
        flashMissingByTestId("dispo-contract-delivery-date");
        return false;
    };

    const send = async () => {
        // Jul-2026 (User-Wunsch) — Auslieferungstermin ist PFLICHTFELD:
        // ohne Termin kein Vertragsversand; Feld wird angesprungen & animiert.
        if (!requireDeliveryDate()) return;
        setSending(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/contract/send`, { include_selbstauskunft: withSA, create_workshop_order_now: createWoNow });
            setState(r.data.dispo_contract);
            toast.success(`Kaufvertrag ${r.data.dispo_contract.contract_number} versendet · E-Mail ${r.data.dispo_contract.email_mocked ? "(Demo/Mock)" : "gesendet"}`);
            toast.message("Dokumente (Kaufvertrag, Selbstauskunft) wurden automatisch mit den aktuellen Daten neu erzeugt.");
            if (r.data.workshop_order) toast.message(`Auslieferungsauftrag ${r.data.workshop_order.order_number || ""} erstellt (intern).`);
            await onSaved?.();
            // Jun-2026 (User-Wunsch · Msg 509) — nach dem Senden direkt zu „Upload Belege".
            if (onSent) {
                toast.message("Weiter zu Upload Belege …");
                onSent();
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
        } finally { setSending(false); }
    };

    // Jun-2026 (User-Wunsch) — ein Klick: bei gesetztem Haken zuerst reservieren
    // (+ Reservierungs-E-Mail), dann Kaufvertrag an Käufer senden.
    const sendCombined = async () => {
        if (reserveOnSend) {
            await reserve();
        }
        await send();
    };

    const simulateSign = async () => {
        setSigning(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/contract/simulate-sign`);
            setState(r.data.dispo_contract);
            toast.success("Signatur simuliert (Demo) — Vertrag unterschrieben");
            await onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Simulation fehlgeschlagen");
        } finally { setSigning(false); }
    };

    const [skribbleBusy, setSkribbleBusy] = useState(false);
    // Jul-2026 (User-Wunsch) — Fernsignatur über Dienstleister (Skribble) wieder
    // als eigene Option: sendet Kaufvertrag + Selbstauskunft an den Käufer und
    // fordert die eIDAS-Fernsignatur an (live mit Skribble-Keys, sonst gemockt).
    const startSkribble = async () => {
        if (!requireDeliveryDate()) return;
        setSkribbleBusy(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/contract/send`, { include_selbstauskunft: withSA, create_workshop_order_now: false });
            setState(r.data.dispo_contract);
            toast.success("Fernsignatur per Skribble angefordert — der Kunde erhält einen Signatur-Link per E-Mail.");
            await onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Skribble-Anforderung fehlgeschlagen");
        } finally { setSkribbleBusy(false); }
    };

    const openSignPortal = async (docType) => {
        setPortalBusy(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/sign-portal`, { doc_type: docType });
            setSignPortal(r.data);
            toast.success("Signatur-Link erstellt — QR am Kundengerät scannen");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Link konnte nicht erstellt werden");
        } finally { setPortalBusy(false); }
    };

    const sendSignPortal = async (channel) => {
        if (!signPortal?.doc_type) return;
        setPortalBusy(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/sign-portal/send`, { doc_type: signPortal.doc_type, channel });
            toast.success(r.data?.message || "Link gesendet");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Senden fehlgeschlagen");
        } finally { setPortalBusy(false); }
    };

    const signed = state?.skribble_status === "signed";
    const canUse = hasCustomer || localCust || Boolean(state);

    return (
        <>
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="w-[96vw] max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="dispo-modal-contract">
                <FeatureGuideTriangle guideKey="contract" />
                <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSignature className="w-5 h-5 text-orange-600" /> Kaufvertrag &amp; digitale Signatur<span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-gray-400 mr-6">Schritt 5 von 6</span></DialogTitle></DialogHeader>
                <AutosaveBadge status={draftStatus} savedAt={draftSavedAt} />

                {!canUse && (
                    <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900" data-testid="dispo-contract-need-customer">
                        Bitte zuerst die <b>Käuferdaten</b> erfassen, dann kann der Kaufvertrag erzeugt werden.
                    </div>
                )}

                {/* Jun-2026 (User-Wunsch) — 2-spaltig & breiter, damit kein vertikales
                    Scrollen mehr nötig ist. Links: Dokumente, Selbstauskunft & Reservierung ·
                    Rechts: Auslieferungstermin & Vertrags-/Signatur-Status. */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-5 gap-y-2 items-start">
                  <div className="space-y-2 min-w-0">

                {/* Digitale Signatur + Auslieferungsauftrag → nach rechts unten verschoben (Jul-2026, User-Wunsch). */}

                {/* Jun-2026 (User-Wunsch · Msg 509) — Unverbindlich reservieren (aus dem
                    Zahlungs-Modal hierher verschoben). Versendet die Reservierungs-E-Mail
                    inkl. Kaufvertrag/Datenschutz/AGB (+ Selbstauskunft bei Finanzierung). */}
                {/* Aug-2026 (User-Wunsch): Reservierung & Freigabe schließen sich
                    gegenseitig aus — bei angehaktem Freigabe-Haken ausgeblendet. */}
                {!approvalChecked && (
                <div className="bg-orange-50 border border-orange-200 rounded-sm p-3 space-y-2" data-testid="dispo-contract-reserve-block">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Clock className="w-4 h-4 text-orange-700 shrink-0" />
                        <span className="text-xs font-bold text-orange-800">Unverbindlich reservieren</span>
                        <Input type="number" min="1" step="1" value={resHours} onChange={(e) => setResHours(e.target.value)} className="w-20 h-8 text-xs font-mono-num" data-testid="dispo-contract-reserve-hours" />
                        <span className="text-xs text-orange-700">Std.</span>
                        <button type="button" onClick={() => setResInfo((s) => !s)} title="Was wird mit der E-Mail versendet?" aria-label="Info zur Reservierungs-E-Mail"
                            className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-orange-300 text-orange-600 hover:bg-orange-100" data-testid="dispo-contract-reserve-info-btn">
                            <Info className="w-3 h-3" />
                        </button>
                        <button type="button" onClick={openResMsg} className="text-[11px] font-bold text-orange-700 hover:underline inline-flex items-center gap-1" data-testid="dispo-contract-reserve-msg-toggle">
                            <Mail className="w-3.5 h-3.5" /> E-Mail-Text einsehen / ändern
                        </button>
                        {gearsOk && <button type="button" onClick={openResSettings} title="Reservierungs-Regeln (max. Dauer, Verlängerung) — gelten für alle Standorte"
                            className="ml-auto p-1 rounded-sm text-orange-500 hover:text-orange-800 hover:bg-orange-100 border border-transparent hover:border-orange-200" data-testid="dispo-contract-reserve-gear">
                            <Settings className="w-4 h-4" />
                        </button>}
                    </div>
                    {resSettingsOpen && (
                        <div className="bg-white border border-orange-200 rounded-sm px-2.5 py-2 space-y-1.5" data-testid="dispo-contract-reserve-settings">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-orange-700">Reservierungs-Regeln (alle Standorte)</div>
                            <div className="text-[11px] text-gray-600">Beim „An Käufer senden" wird für die angegebene Dauer reserviert und die Reservierungs-E-Mail (Kaufvertrag, Datenschutz, AGB{hasFinancing ? ", Selbstauskunft" : ""}) versendet. Der Verkäufer kann eine aktive Reservierung <b>einmalig</b> verlängern — der Kunde wird automatisch informiert.</div>
                            <div className="flex flex-wrap items-center gap-3">
                                <label className="text-[11px] flex items-center gap-1.5">Max. Reservierungsdauer
                                    <Input type="number" min="0.5" step="0.5" value={resSettings?.max_months ?? ""} onChange={(e) => setResSettings((p) => ({ ...p, max_months: e.target.value }))} onBlur={saveResSettings} className="w-16 h-7 text-xs font-mono-num" data-testid="dispo-reserve-max-months" /> Monate
                                </label>
                                <label className="text-[11px] flex items-center gap-1.5">Einmalige Verlängerung um
                                    <Input type="number" min="0.5" step="0.5" value={resSettings?.extension_months ?? ""} onChange={(e) => setResSettings((p) => ({ ...p, extension_months: e.target.value }))} onBlur={saveResSettings} className="w-16 h-7 text-xs font-mono-num" data-testid="dispo-reserve-extension-months" /> Monate
                                </label>
                            </div>
                        </div>
                    )}
                    {/* Jun-2026 (User-Wunsch) — „Reservieren & E-Mail" jetzt als Haken;
                        der separate Button entfällt und geht im Footer-Button
                        „An Käufer senden" auf (ein Klick = reservieren + senden). */}
                    <label className="flex items-start gap-2 cursor-pointer bg-white border border-orange-200 rounded-sm px-2.5 py-2" data-testid="dispo-contract-reserve-onsend-block">
                        <input type="checkbox" checked={reserveOnSend} onChange={(e) => setReserveOnSend(e.target.checked)} disabled={!canUse} className="mt-0.5" data-testid="dispo-contract-reserve-onsend" />
                        <span className="text-[12px] text-orange-900">
                            <span className="font-bold">Reservieren &amp; E-Mail</span> — beim Klick auf „An Käufer senden" wird das Fahrzeug zusätzlich für die angegebene Dauer reserviert und die Reservierungs-E-Mail versendet.
                        </span>
                    </label>
                    {resInfo && (
                        <div className="text-[11px] text-orange-900 bg-white border border-orange-200 rounded-sm px-2.5 py-1.5 flex items-start gap-1.5" data-testid="dispo-contract-reserve-info">
                            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-orange-500" />
                            <span>Der Käufer erhält eine Bestätigungs-E-Mail. Mitgesendet werden <b>Kaufvertrag, Datenschutzhinweise und AGB</b>{hasFinancing ? <> sowie bei Zahlungsart <b>Finanzierung</b> das <b>Selbstauskunft-Formular</b></> : ""}. Den Begleittext können Sie über „E-Mail-Text" anpassen.</span>
                        </div>
                    )}
                    {showResMsg && (
                        <div className="space-y-1.5" data-testid="dispo-contract-reserve-msg-editor">
                            {/* Jul-2026 (User-Wunsch): Text-Ansicht deutlich hervorgehoben (einheitlicher Stil). */}
                            <Textarea value={resMsg} onChange={(e) => setResMsg(e.target.value)} rows={14} className="text-[13px] leading-relaxed bg-white border-2 border-orange-300 focus:border-orange-500 shadow-sm" placeholder="E-Mail-Begleittext…" data-testid="dispo-contract-reserve-msg" />
                            <Button type="button" size="sm" variant="outline" onClick={resAiImprove} disabled={resAiBusy} className="h-7 text-[11px] border-2" data-testid="dispo-contract-reserve-msg-ai">
                                {resAiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                                KI: Text überarbeiten
                            </Button>
                        </div>
                    )}
                    {/* Aktiver Countdown (eigene Zeile). */}
                    {resActive && (
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[12px] text-orange-900 inline-flex items-center gap-1" data-testid="dispo-contract-reserve-countdown">
                                <Clock className="w-3.5 h-3.5 text-orange-600 shrink-0" />
                                <span>Reserviert bis <b>{fmtUntil(reservedUntil)} Uhr</b> — noch <b className="font-mono-num">{fmtRemaining(resRemainingMs)}</b></span>
                            </span>
                            {resExtended ? (
                                <span className="text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-500 border border-gray-200 rounded-sm px-1.5 py-0.5" data-testid="dispo-contract-reserve-extended-badge">bereits einmalig verlängert</span>
                            ) : (
                                <Button type="button" size="sm" variant="outline" onClick={extendReservation} disabled={extending}
                                    className="h-7 text-[11px] border-2 border-orange-300 text-orange-800" data-testid="dispo-contract-reserve-extend-btn">
                                    {extending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Clock className="w-3.5 h-3.5 mr-1" />}
                                    Einmalig verlängern (+{Number(resSettings?.extension_months || 1)} Monat{Number(resSettings?.extension_months || 1) === 1 ? "" : "e"})
                                </Button>
                            )}
                        </div>
                    )}
                    {!resActive && reservedUntil ? (
                        <div className="bg-white border border-red-300 rounded-sm px-2.5 py-2 flex items-center gap-2" data-testid="dispo-contract-reserve-expired">
                            <Clock className="w-4 h-4 text-red-600 shrink-0" />
                            <span className="text-[12px] text-red-700">Reservierung abgelaufen ({fmtUntil(reservedUntil)} Uhr)</span>
                        </div>
                    ) : null}
                </div>
                )}

                {/* Jun-2026 (User-Wunsch) — Freigabe des GESAMTGESCHÄFTS direkt unter
                    „Unverbindlich reservieren". Haken fix (verpflichtend).
                    Jul-2026: Bei „Unverbindlich reservieren" ausgeblendet — Genehmigung
                    nur für verbindliche Bestellungen ohne Reservierung. */}
                {!reserveOnSend && (
                <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 space-y-2" data-testid="dispo-contract-deal-approval">
                    <label className="flex items-center gap-2 text-xs cursor-pointer text-gray-700" data-testid="dispo-contract-approval-label">
                        <input
                            type="checkbox"
                            checked={approvalChecked}
                            onChange={(e) => setApprovalChecked(e.target.checked)}
                            className="accent-orange-500"
                            data-testid="dispo-contract-approval-checkbox"
                        />
                        <span className="font-bold">Auf Genehmigung und Freigabe warten</span> (Gesamtgeschäft)
                    </label>
                    {approvalChecked && (<>
                    {dealApproval?.status === "approved" ? (
                        <div className="space-y-2" data-testid="dispo-contract-approval-approved">
                            <div className="inline-flex items-center gap-1 text-emerald-700 font-bold text-xs">
                                <CheckCircle2 className="w-4 h-4" /> Gesamtgeschäft freigegeben
                            </div>
                            <div className="bg-white border border-emerald-200 rounded-sm px-2.5 py-2 space-y-1.5">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <span className="text-[11px] text-gray-600" data-testid="dispo-contract-oc-status">
                                        {dealApproval?.confirmation_sent_at
                                            ? <>Auftragsbestätigung versendet am <b>{new Date(dealApproval.confirmation_sent_at).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })} Uhr</b></>
                                            : "Auftragsbestätigung: Noch nicht versendet."}
                                    </span>
                                    <button type="button" onClick={() => { if (!showOcMsg && !ocMsg) setOcMsg(dealApproval?.confirmation_message || ""); setShowOcMsg((v) => !v); }} className="text-[11px] font-bold text-emerald-700 hover:underline inline-flex items-center gap-1" data-testid="dispo-contract-oc-toggle">
                                        <Mail className="w-3.5 h-3.5" /> E-Mail-Text einsehen / ändern
                                    </button>
                                </div>
                                {showOcMsg && (
                                    <Textarea value={ocMsg} onChange={(e) => setOcMsg(e.target.value)} rows={6} className="text-[13px] leading-relaxed bg-white border-2 border-emerald-300 focus:border-emerald-500 shadow-sm" placeholder="Zusätzlicher Text für die Auftragsbestätigung (optional)…" data-testid="dispo-contract-oc-msg" />
                                )}
                                <Button type="button" size="sm" onClick={sendOrderConfirmation} disabled={ocBusy} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dispo-contract-oc-send">
                                    {ocBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Mail className="w-3.5 h-3.5 mr-1" /> {dealApproval?.confirmation_sent_at ? "Auftragsbestätigung erneut senden" : "Auftragsbestätigung senden"}</>}
                                </Button>
                            </div>
                        </div>
                    ) : dealApproval?.status === "pending" ? (
                        <div className="flex items-center gap-2 flex-wrap" data-testid="dispo-contract-approval-pending">
                            <span className="inline-flex items-center gap-1 text-orange-700 font-bold text-xs">
                                <Clock className="w-4 h-4" /> Freigabe angefordert — wartet auf Filialleiter/Verkaufsleiter
                            </span>
                            <Button type="button" size="sm" variant="outline" onClick={() => requestDealApproval()} disabled={dealApprovalBusy} className="h-8 text-xs border-2 ml-auto" data-testid="dispo-contract-approval-remind">
                                {dealApprovalBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Bell className="w-3.5 h-3.5 mr-1" /> Erinnerung senden</>}
                            </Button>
                        </div>
                    ) : dealApproval?.status === "rejected" ? (
                        <div className="space-y-1.5" data-testid="dispo-contract-approval-rejected">
                            <span className="inline-flex items-center gap-1 text-red-700 font-bold text-xs">
                                <AlertTriangle className="w-4 h-4" /> Freigabe abgelehnt — Unterlagen fehlerhaft
                            </span>
                            {dealApproval?.rejected_reason && (
                                <div className="bg-red-50 border border-red-200 rounded-sm px-2.5 py-2 text-xs text-red-800" data-testid="dispo-contract-approval-reject-reason">
                                    <b>Ablehnungsgrund:</b> {dealApproval.rejected_reason}
                                </div>
                            )}
                            <div className="text-[11px] text-gray-600">
                                Bitte die Unterlagen im Upload-Portal korrigieren — die Freigabe wird danach <b>automatisch neu angefordert</b>.
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-1.5">
                            <div className="bg-white border border-orange-200 rounded-sm px-2.5 py-2 text-[11px] text-gray-700" data-testid="dispo-contract-approval-auto-hint">
                                Die Freigabe wird <b>automatisch</b> beim Filialleiter angefordert, sobald <b>alle Pflicht-Unterlagen</b> im Upload-Portal vorhanden sind.
                            </div>
                            <Button type="button" size="sm" variant="outline" onClick={() => requestDealApproval()} disabled={dealApprovalBusy} className="h-8 text-xs border-2" data-testid="dispo-contract-approval-request">
                                {dealApprovalBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Freigabe jetzt anfordern"}
                            </Button>
                        </div>
                    )}
                    <div className="text-[11px] text-gray-500">
                        Vor Abschluss muss das Gesamtgeschäft genehmigt &amp; freigegeben werden. Haken abwählen blendet stattdessen „Unverbindlich reservieren" ein.
                    </div>
                    {/* Aug-2026 (User-Wunsch) — Freigabe-Verlauf: alle Anforderungen,
                        Freigaben & Ablehnungen mit Grund, Datum und Prüfer. */}
                    {approvalHistory.length > 0 && (
                        <div className="border-t border-gray-200 pt-2" data-testid="dispo-approval-history">
                            <button
                                type="button"
                                onClick={() => setHistoryOpen((v) => !v)}
                                className="w-full text-left text-[10px] font-bold uppercase tracking-wider text-gray-500 hover:text-[#111827] flex items-center gap-1.5"
                                data-testid="dispo-approval-history-toggle"
                                aria-expanded={historyOpen}
                            >
                                <History className="w-3.5 h-3.5" /> Freigabe-Verlauf ({approvalHistory.length})
                                <ArrowRight className={`w-3 h-3 ml-auto transition-transform ${historyOpen ? "rotate-90" : ""}`} />
                            </button>
                            {historyOpen && (
                                <ul className="mt-1.5 space-y-1.5 max-h-48 overflow-y-auto pr-1" data-testid="dispo-approval-history-list">
                                    {approvalHistory.map((h, i) => {
                                        const kindLabel = { deal: "Gesamtgeschäft", tradein: "Ankauf", discount: "Rabatt" }[h.kind] || h.kind;
                                        const cfg = {
                                            requested: { label: "Angefordert", cls: "text-orange-700 bg-orange-50 border-orange-200" },
                                            approved: { label: "Freigegeben", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
                                            rejected: { label: "Abgelehnt", cls: "text-red-700 bg-red-50 border-red-200" },
                                        }[h.action] || { label: h.action, cls: "text-gray-600 bg-gray-50 border-gray-200" };
                                        return (
                                            <li key={i} className="bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-[11px]" data-testid={`dispo-approval-history-item-${i}`}>
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <span className={`font-bold text-[10px] uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${cfg.cls}`}>{cfg.label}</span>
                                                    <span className="font-bold text-gray-700">{kindLabel}</span>
                                                    <span className="text-gray-400 font-mono-num ml-auto">
                                                        {h.at ? new Date(h.at).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }) + " Uhr" : ""}
                                                    </span>
                                                </div>
                                                <div className="text-gray-600 mt-0.5">
                                                    {h.action === "requested"
                                                        ? (h.auto ? "Automatisch (alle Unterlagen vollständig)" : `Angefordert von ${h.actor_name || "—"}`)
                                                        : <>Prüfer: <b>{h.actor_name || "—"}</b></>}
                                                </div>
                                                {h.reason && (
                                                    <div className="text-red-800 bg-red-50 border border-red-100 rounded-sm px-1.5 py-1 mt-1">
                                                        <b>Grund:</b> {h.reason}
                                                    </div>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    )}
                    </>)}
                </div>
                )}

                {/* Auslieferungstermin — nach LINKS ganz unten verschoben (User-Wunsch Jul-2026). */}
                <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 space-y-2" data-testid="dispo-contract-delivery-block">
                    <div className="flex items-center justify-between">
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Auslieferungstermin <span className="text-red-600" title="Pflichtfeld">*</span></label>
                        {deliverySaving && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <Input
                            type="date"
                            value={deliveryDate}
                            onChange={(e) => { setDeliveryDate(e.target.value); saveDelivery(e.target.value, deliveryBinding); }}
                            className="rounded-sm h-9 w-44 text-sm"
                            data-testid="dispo-contract-delivery-date"
                        />
                        <label className="flex items-center gap-2 text-xs cursor-not-allowed text-gray-600" data-testid="dispo-contract-delivery-binding-label">
                            <input
                                type="checkbox"
                                checked
                                disabled
                                readOnly
                                className="accent-orange-500"
                                data-testid="dispo-contract-delivery-unverbindlich"
                            />
                            Unverbindlich (Bestellfahrzeug)
                        </label>
                    </div>
                    <div className="text-[11px] text-gray-500">
                        Der Auslieferungstermin ist vorerst <b>unverbindlich</b> (Bestellfahrzeug) und wird so im Kaufvertrag ausgewiesen — diese Einstellung ist derzeit nicht veränderbar.
                    </div>
                </div>
                  </div>
                  <div className="space-y-2 min-w-0">

                {/* Dokumente / Vorschau — rechts unten (User-Wunsch Jul-2026). */}
                <div className="space-y-2 pt-1 border-t border-gray-200" data-testid="dispo-contract-documents-block">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-700">Dokumente</div>
                    {state?.sent_at && state?.data_changed && (
                        <div className="text-[11px] font-bold text-amber-900 bg-amber-50 border-l-4 border-amber-500 rounded-sm px-3 py-2 flex flex-wrap items-center gap-2" data-testid="dispo-contract-data-changed-banner">
                            <span className="flex-1 min-w-[200px]">⚠ Verkaufsdaten wurden nach dem Vertragsversand geändert — bitte Sammel-PDF neu erzeugen und den Vertrag erneut an den Kunden senden.</span>
                            <Button type="button" size="sm" className="h-8 text-[11px] bg-amber-600 hover:bg-amber-700 text-white"
                                onClick={() => openPdf(`/listings/${listingId}/dispo/documents-bundle.pdf?ts=${Date.now()}`, "dokumente-sammel.pdf")}
                                data-testid="dispo-contract-regenerate-bundle">
                                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Sammel-PDF neu erzeugen
                            </Button>
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                        <Button type="button" variant="outline" size="sm" className="border-2 h-9 text-xs" disabled={!canUse}
                            onClick={() => openPdf(`/listings/${listingId}/dispo/contract.pdf`, "kaufvertrag.pdf")} data-testid="dispo-contract-preview">
                            <FileText className="w-4 h-4 mr-1" /> Kaufvertrag (Vorschau)
                        </Button>
                        {hasTradein && (
                            <Button type="button" variant="outline" size="sm" className="border-2 h-9 text-xs"
                                onClick={() => openPdf(`/listings/${listingId}/dispo/tradein/contract.pdf`, "ankaufvertrag.pdf")} data-testid="dispo-contract-ankauf-preview">
                                <FileText className="w-4 h-4 mr-1" /> Ankaufvertrag (Vorschau)
                            </Button>
                        )}
                        {hasFinancing && (
                            <Button type="button" variant="outline" size="sm" className="border-2 h-9 text-xs"
                                onClick={() => openPdf(`/listings/${listingId}/dispo/selbstauskunft.pdf`, "selbstauskunft.pdf")} data-testid="dispo-contract-sa-preview">
                                <FileText className="w-4 h-4 mr-1" /> Selbstauskunft (Vorschau)
                            </Button>
                        )}
                        {/* Jul-2026 (User-Wunsch) — EU-steuerfreier Verkauf: Pflicht-Dokumente druckbar */}
                        {euTaxFree && (
                            <>
                                <Button type="button" variant="outline" size="sm" className="border-2 h-9 text-xs border-sky-500 text-sky-800"
                                    onClick={() => openPdf(`/listings/${listingId}/dispo/gelangensbestaetigung.pdf?ts=${Date.now()}`, "gelangensbestaetigung.pdf")} data-testid="dispo-contract-gelangen">
                                    <FileText className="w-4 h-4 mr-1" /> 🇪🇺 Gelangensbestätigung (PDF)
                                </Button>
                                {custData?.vat_check?.id && (
                                    <Button type="button" variant="outline" size="sm" className="border-2 h-9 text-xs border-sky-500 text-sky-800"
                                        onClick={() => openPdf(`/tax/vies-checks/${custData.vat_check.id}/proof.pdf`, "ustid-pruefprotokoll.pdf")} data-testid="dispo-contract-vat-proof">
                                        <FileText className="w-4 h-4 mr-1" /> 🇪🇺 USt-IdNr.-Prüfprotokoll (PDF)
                                    </Button>
                                )}
                            </>
                        )}
                        {user?.doc_privacy_url && (
                            <a href={user.doc_privacy_url} target="_blank" rel="noopener noreferrer" data-testid="dispo-contract-privacy">
                                <Button type="button" variant="outline" size="sm" className="border-2 h-9 text-xs">
                                    <FileText className="w-4 h-4 mr-1" /> Datenschutz
                                </Button>
                            </a>
                        )}
                        {user?.doc_terms_url && (
                            <a href={user.doc_terms_url} target="_blank" rel="noopener noreferrer" data-testid="dispo-contract-agb">
                                <Button type="button" variant="outline" size="sm" className="border-2 h-9 text-xs">
                                    <FileText className="w-4 h-4 mr-1" /> AGB
                                </Button>
                            </a>
                        )}
                    </div>
                    <Button type="button" size="sm" className="h-9 text-xs bg-[#111827] hover:bg-black text-white" disabled={!canUse}
                        onClick={() => openPdf(`/listings/${listingId}/dispo/documents-bundle.pdf?ts=${Date.now()}`, "dokumente-sammel.pdf")} data-testid="dispo-contract-bundle">
                        <Printer className="w-4 h-4 mr-1" /> Alles drucken (Sammel-PDF)
                    </Button>
                    <Button type="button" size="sm" variant="outline" className="h-9 text-xs border-2 ml-2" disabled={!canUse}
                        onClick={() => { toast.info("Vertrags-PDF wird mit den aktuellen Daten neu erzeugt…"); openPdf(`/listings/${listingId}/dispo/contract.pdf?ts=${Date.now()}`, "kaufvertrag.pdf"); }}
                        title="Erzeugt das Kaufvertrags-PDF neu mit allen aktuellen Daten des Verkaufsprozesses"
                        data-testid="dispo-contract-refresh-pdf">
                        <RefreshCw className="w-4 h-4 mr-1" /> Vertrags-PDF aktualisieren
                    </Button>
                    <p className="text-[10px] text-gray-500">Sammel-PDF: Kaufvertrag{hasTradein ? " + Ankaufvertrag" : ""}{hasFinancing ? " + Selbstauskunft" : ""} + Datenschutz + AGB — alles auf einmal druckfertig. Die PDFs werden bei jedem Öffnen mit den aktuellen Prozessdaten neu erzeugt.</p>
                </div>

                {/* Jul-2026 (User-Wunsch) — Digitale Signatur + Auslieferungsauftrag ganz nach RECHTS UNTEN verschoben. */}
                <div className="space-y-2 pt-1 border-t border-gray-200" data-testid="dispo-contract-signature-section">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-700">Digitale Signatur</div>
                    <p className="text-[11px] text-gray-500">Der Kunde signiert digital am eigenen oder Ihrem Gerät, im Laden am Handy/Tablet, oder unterschreibt den Ausdruck. Es unterschreibt nur der Kunde (Auftragsbestätigung). Alternativ: <b>Fernsignatur per Skribble</b> (eIDAS) — der Kunde signiert bequem per E-Mail-Link über den Dienstleister.</p>
                    {/* Selbstauskunft (Bank) beilegen — VOR den Signier-Buttons (User-Wunsch Jul-2026 · 451), da sie mitsigniert/versendet wird. Nur bei Finanzierung/Leasing. */}
                    {hasFinancing && (
                        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="dispo-contract-sa-row">
                            <input type="checkbox" checked={withSA} onChange={(e) => setWithSA(e.target.checked)} className="accent-orange-500" data-testid="dispo-contract-with-sa" />
                            Selbstauskunft (Bank) beilegen
                        </label>
                    )}
                    {sigEnabled === true && (
                    <div className="flex flex-wrap gap-2">
                        <Button type="button" size="sm" onClick={() => openSignPortal("contract")} disabled={!canUse || portalBusy || sigEnabled !== true}
                            title={sigEnabled !== true ? "Digitale Signatur ist deaktiviert — in den Händler-Einstellungen (Marktplatz) aktivieren." : undefined}
                            className="h-9 text-xs bg-orange-500 hover:bg-orange-600 text-white disabled:bg-gray-300 disabled:text-gray-500" data-testid="dispo-sign-portal-contract-btn">
                            <PenLine className="w-4 h-4 mr-1" /> Kaufvertrag signieren
                        </Button>
                        {hasTradein && (
                            <Button type="button" size="sm" onClick={() => openSignPortal("tradein")} disabled={portalBusy || sigEnabled !== true}
                                title={sigEnabled !== true ? "Digitale Signatur ist deaktiviert — in den Händler-Einstellungen (Marktplatz) aktivieren." : undefined}
                                className="h-9 text-xs bg-orange-500 hover:bg-orange-600 text-white disabled:bg-gray-300 disabled:text-gray-500" data-testid="dispo-sign-portal-tradein-btn">
                                <PenLine className="w-4 h-4 mr-1" /> Ankaufvertrag signieren
                            </Button>
                        )}
                        <Button type="button" size="sm" onClick={() => openSignPortal("handover")} disabled={!canUse || portalBusy || sigEnabled !== true}
                            title={sigEnabled !== true ? "Digitale Signatur ist deaktiviert — in den Händler-Einstellungen (Marktplatz) aktivieren." : undefined}
                            className="h-9 text-xs bg-orange-500 hover:bg-orange-600 text-white disabled:bg-gray-300 disabled:text-gray-500" data-testid="dispo-sign-portal-handover-btn">
                            <PenLine className="w-4 h-4 mr-1" /> Übergabeprotokoll signieren
                        </Button>
                    </div>
                    )}
                    {sigEnabled === false && (
                        <div className="space-y-2" data-testid="dispo-sign-disabled-block">
                            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1" data-testid="dispo-sign-disabled-hint">
                                Digitale Signatur ist nicht aktiviert — der Kunde unterschreibt den Ausdruck. Laden Sie die unterschriebenen Dokumente anschließend im Upload-Portal hoch. (Aktivierung: Händler-Einstellungen → Marktplatzeinstellungen → „Digitale Signatur aktivieren".)
                            </p>
                            <Button type="button" size="sm" onClick={() => (onContinue ? onContinue() : onClose?.())}
                                className="h-9 text-xs bg-orange-500 hover:bg-orange-600 text-white" data-testid="dispo-sign-upload-portal-btn">
                                <FileText className="w-4 h-4 mr-1" /> Upload-Portal öffnen — unterschriebene Dokumente hochladen
                            </Button>
                        </div>
                    )}
                    {signPortal && (
                        <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-3 space-y-2" data-testid="dispo-sign-portal-box">
                            <div className="text-xs font-bold text-indigo-900">{signPortal.doc_type === "tradein" ? "Ankaufvertrag" : signPortal.doc_type === "handover" ? "Übergabeprotokoll" : "Kaufvertrag"} signieren</div>
                            <Button type="button" size="sm" variant="outline" onClick={() => setSigModal(signPortal.doc_type)} className="h-8 text-xs border-2 border-orange-300 text-orange-700 hover:bg-orange-50" data-testid="dispo-contract-sign-onsite-btn">
                                <PenLine className="w-4 h-4 mr-1" /> Auf diesem Gerät signieren (Touchscreen/Tablet im Laden)
                            </Button>
                            <div className="flex items-center gap-3 pt-1 border-t border-indigo-200">
                                <img src={signPortal.qr_url} alt="QR" className="w-24 h-24 bg-white border border-indigo-200 rounded-sm p-1" data-testid="dispo-sign-portal-qr" />
                                <div className="flex-1 min-w-0 space-y-2">
                                    <p className="text-[11px] text-indigo-900">QR am Kunden-Handy/Tablet scannen oder den Link öffnen. Sicherheitsabfrage: Nachname + Geburtsdatum des Kunden.</p>
                                    <div className="flex items-center gap-2">
                                        <input readOnly value={signPortal.portal_url} className="flex-1 text-[11px] font-mono bg-white border border-indigo-200 rounded-sm px-2 py-1 truncate" data-testid="dispo-sign-portal-url" />
                                        <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={() => { navigator.clipboard?.writeText(signPortal.portal_url); toast.success("Link kopiert"); }} data-testid="dispo-sign-portal-copy">Kopieren</Button>
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-900 mr-1">An Kunden senden:</span>
                                        <Button type="button" size="sm" variant="outline" className="h-7 text-[11px] border-indigo-300" disabled={portalBusy} onClick={() => sendSignPortal("email")} data-testid="dispo-sign-portal-send-email"><Mail className="w-3.5 h-3.5 mr-1" /> E-Mail</Button>
                                        <Button type="button" size="sm" variant="outline" className="h-7 text-[11px] border-indigo-300" disabled={portalBusy} onClick={() => sendSignPortal("sms")} data-testid="dispo-sign-portal-send-sms"><Send className="w-3.5 h-3.5 mr-1" /> SMS</Button>
                                        <Button type="button" size="sm" variant="outline" className="h-7 text-[11px] border-emerald-300 text-emerald-700" disabled={portalBusy} onClick={() => sendSignPortal("whatsapp")} data-testid="dispo-sign-portal-send-whatsapp"><Send className="w-3.5 h-3.5 mr-1" /> WhatsApp</Button>
                                    </div>
                                    <span className="text-[10px] text-indigo-700 block">Beim Versand werden AGB &amp; Datenschutz als Links und die PDFs als Anhang mitgeschickt. Alternativ QR auf Ihrem eigenen Handy öffnen (falls Kunde kein Handy hat).</span>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Jul-2026 (User-Wunsch) — Auslieferungsauftrag wird nach Signatur IMMER automatisch erzeugt. */}
                    <div className="flex items-start gap-2 text-sm pt-1" data-testid="dispo-contract-early-wo-row">
                        <input type="checkbox" checked readOnly disabled className="accent-orange-500 mt-0.5" data-testid="dispo-contract-early-wo" />
                        <span>
                            Auslieferungsauftrag erzeugen (automatisch)
                            <span className="block text-[11px] text-gray-500">Der Werkstattauftrag für Zubehör/Dienstleistungen wird automatisch erstellt, sobald der Kaufvertrag digital signiert oder der unterschriebene Vertrag im Upload-Portal hochgeladen wurde — und erscheint direkt zur Bearbeitung in den Werkstatt-Aufträgen.</span>
                        </span>
                    </div>
                </div>
                  </div>
                </div>

                <DialogFooter className="gap-2">
                    <DispoBackButton onBack={onBack} />
                    <Button type="button" variant="outline" onClick={onClose}>Schließen</Button>
                    <Button type="button" variant="outline" onClick={() => saveDelivery(deliveryDate, deliveryBinding, { silent: false })} disabled={deliverySaving} className="border-2" data-testid="dispo-contract-update">
                        {deliverySaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1" /> Aktualisieren</>}
                    </Button>
                    {state && state.skribble_is_mocked && !signed && (
                        <Button type="button" variant="outline" onClick={simulateSign} disabled={signing} className="border-2" data-testid="dispo-contract-simulate-sign">
                            {signing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Signatur simulieren (Demo)"}
                        </Button>
                    )}
                    <Button type="button" onClick={reserveOnSend ? sendCombined : send} disabled={sending || reserving || !canUse} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="dispo-contract-send">
                        {sending || reserving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" />{reserveOnSend ? "Reservieren & An Käufer senden" : (state ? "Erneut senden" : "An Käufer senden")}</>}
                    </Button>
                    {onContinue && (
                        <Button type="button" onClick={() => onContinue()} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dispo-contract-continue">
                            <ArrowRight className="w-4 h-4 mr-1" /> Weiter zu Upload Belege
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
        {sigModal && (
            <OnSiteSignatureModal
                title={sigModal === "tradein" ? "Ankaufvertrag — Unterschrift Kunde" : sigModal === "handover" ? "Übergabeprotokoll — Unterschriften" : "Kaufvertrag — Unterschrift Kunde"}
                fields={sigModal === "handover"
                    ? [{ key: "buyer", label: "Unterschrift Kunde" }, { key: "seller", label: "Unterschrift Auslieferer (optional)" }]
                    : [{ key: "buyer", label: "Unterschrift Kunde" }]}
                busy={signingOnsite}
                onClose={() => setSigModal(null)}
                onSubmit={submitOnsite}
            />
        )}
        </>
    );
}
